import {
  ReloginContext,
  setOnUnauthorized,
  setTokenRefresher,
  setTokenValidator,
} from "@inrange/building-manager-api-client";
import { KindeOrg } from "@inrange/building-manager-api-client/models-organisation";
import {
  KINDE_ENVIRONMENT,
  makeEnsureFreshToken,
  makeTokenValidator,
  useRejectForeignTokenOnInitialLoad,
} from "@inrange/shared-components";
import { Loading } from "@inrange/theme-components";
import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import * as Sentry from "@sentry/react";
import {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { hotjar } from "react-hotjar";

interface User {
  name: string;
  email: string;
  family_name: string;
  given_name: string;
  picture: string;
  kinde_org_code: string;
  org_id?: string;
  kinde_orgs: KindeOrg[];
}

interface UserContextValue {
  user?: User;
  logout?: () => void;
  onOrgSwitch?: (id: string) => void;
}

const UserContext = createContext<UserContextValue>({});

const UserProvider = ({ children }: { children: ReactNode }) => {
  const {
    login,
    logout,
    isLoading: isAuthLoading,
    user: kindeUser,
    getClaim: getKindeTokenClaim,
    getToken,
    refreshToken,
  } = useKindeAuth();

  const [user, setUser] = useState<User | undefined>(undefined);
  useEffect(() => {
    if (kindeUser && getKindeTokenClaim) {
      const doSetUser = async () => {
        const orgIdClaim = await getKindeTokenClaim("external_org_id");
        const orgId = orgIdClaim ? (orgIdClaim.value as string) : undefined;
        const kindeOrgCode = (await getKindeTokenClaim("org_code"))!
          .value as string;
        const kindeOrgs = (await getKindeTokenClaim("organizations", "idToken"))!
          .value as unknown as KindeOrg[];
        setUser({
          name: `${kindeUser.givenName} ${kindeUser.familyName}`,
          email: kindeUser.email!,
          family_name: kindeUser.familyName!,
          given_name: kindeUser.givenName!,
          picture: kindeUser.picture!,
          kinde_org_code: kindeOrgCode,
          org_id: orgId,
          kinde_orgs: kindeOrgs,
        });
        if (import.meta.env.PROD) {
          Sentry.setUser({
            email: kindeUser.email!,
          });
        }
        const emailDomain = kindeUser?.email?.split("@")[1];
        if (import.meta.env.PROD && emailDomain !== "inrange.io") {
          if (!hotjar.initialized()) {
            hotjar.initialize({ id: 3032509, sv: 6 });
          }
        }
      };
      doSetUser().catch((error) => {
        // Kinde v5 getClaim is async and can reject (token decode/refresh
        // failure), and a missing required claim makes the non-null assertions
        // above throw; the sync v3 calls could neither reject nor be awaited.
        // Without this catch the rejection is unhandled and `user` never
        // resolves, hanging the app on the Loading spinner forever. Surface it
        // and re-trigger login to recover the broken session (mirrors the
        // admin app's getOrganization().catch handling).
        Sentry.captureException(error);
        login({
          state: {
            redirectTo: window.location.pathname + window.location.search,
          },
        });
      });
    }
  }, [kindeUser, getKindeTokenClaim, login]);

  const { setRelogin } = useContextTS(ReloginContext);
  const reloginStartedRef = useRef(false);
  const relogin = useCallback(() => {
    if (!reloginStartedRef.current) {
      reloginStartedRef.current = true;
      console.log("Starting relogin flow");
      login({
        state: { redirectTo: window.location.pathname + window.location.search },
      });
    }
    // Never-resolving promise because the login will trigger a full page redirect
    return new Promise<void>((_resolve) => {});
  }, [login]);
  useEffect(() => {
    setRelogin(() => relogin);
  }, [relogin, setRelogin]);

  // Wire axios response 401 interceptor to trigger relogin. Without this, a
  // signed-out user's inflight React Query polls (e.g. useSitePR) keep firing
  // 401s silently — this is the root cause of CUSTOMER-APP-P (~12k events).
  useEffect(() => {
    setOnUnauthorized(relogin);
    return () => setOnUnauthorized(null);
  }, [relogin]);

  // Proactively refresh the Kinde access token before each BMA request when
  // it's near expiry. Kinde v5's getToken() no longer refreshes on its own, so
  // without this an access token that expires while this tab stays focused is
  // never renewed and every request 401s until a full reload (see
  // ensureFreshKindeToken + getAuthTokenBlocking).
  useEffect(() => {
    setTokenRefresher(
      makeEnsureFreshToken(
        getToken,
        refreshToken,
        KINDE_ENVIRONMENT.KINDE_CUSTOMER_APP_CLIENT_ID
      )
    );
    return () => setTokenRefresher(null);
  }, [getToken, refreshToken]);

  // Gate API calls on token *validity*, not just presence, so a present-but-
  // invalid token (expired, or minted by another client) blocks on relogin
  // instead of 401-looping (see getAuthTokenBlocking / makeTokenValidator).
  const validateToken = useMemo(
    () => makeTokenValidator(KINDE_ENVIRONMENT.KINDE_CUSTOMER_APP_CLIENT_ID),
    []
  );
  useEffect(() => {
    setTokenValidator(validateToken);
    return () => setTokenValidator(null);
  }, [validateToken]);

  // Reject a foreign-client token on cold load and re-authenticate (restores the
  // v3 SDK behaviour the v5 upgrade dropped - see the hook for the full story).
  // Safe to redirect here because the first token resolves while we're still on
  // the Loading screen; it mints our own client's token off the SSO session.
  const triggerReauth = useCallback(() => {
    login({
      state: { redirectTo: window.location.pathname + window.location.search },
    });
  }, [login]);
  const initialAuthOk = useRejectForeignTokenOnInitialLoad({
    ready: !isAuthLoading && !!kindeUser,
    getToken,
    validateToken,
    triggerReauth,
  });

  if (!isAuthLoading && !kindeUser) {
    login({
      state: { redirectTo: window.location.pathname + window.location.search },
    });
  }

  if (!user || !initialAuthOk) {
    return <Loading label="Loading ..." />;
  }

  const onOrgSwitch = (id: string) => {
    const thisOrg = id === user.kinde_org_code;
    if (thisOrg) return;
    login({
      orgCode: id,
      state: { redirectTo: window.location.pathname + window.location.search },
    });
  };

  return (
    <UserContext.Provider value={{ user, logout, onOrgSwitch }}>
      {children}
    </UserContext.Provider>
  );
};

export { UserContext, UserProvider };
